import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';

interface Coupon {
  periodNumber: number;
  exDate: string;
  paymentDate: string;
  amount: string;
  isPaid: boolean;
  isAutocalled: boolean;
}

interface ProductCouponsTableProps {
  coupons: Coupon[];
  currency: string;
  showExDate?: boolean;
}

export const ProductCouponsTable: React.FC<ProductCouponsTableProps> = ({ coupons, currency, showExDate = true }) => {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Find next unpaid coupon
  const nextCoupon = coupons.find((c) => !c.isPaid);

  return (
    <View style={[styles.container, Platform.OS === 'web' && { padding: 16, marginBottom: 12 }]}>
      <Text style={styles.sectionTitle}>Coupons</Text>
      <View style={styles.separator} />

      {/* Next coupon */}
      {nextCoupon && (
        <View style={styles.nextCouponContainer}>
          <Text style={styles.nextCouponLabel}>Next coupon</Text>
          <View style={styles.dateRow}>
            <View style={[styles.dateIndicator, { backgroundColor: '#4CAF50' }]} />
            <Text style={styles.nextCouponDate}>{formatDate(nextCoupon.paymentDate)}</Text>
          </View>
        </View>
      )}

      {/* Coupon list */}
      {coupons.map((coupon, index) => (
        <View key={index} style={styles.couponItem}>
          <View style={styles.couponRow}>
            {showExDate && (
              <View style={styles.couponField}>
                <Text style={styles.couponLabel}>Ex date</Text>
                <Text style={styles.couponValue}>{formatDate(coupon.exDate)}</Text>
              </View>
            )}
            <View style={styles.couponField}>
              <Text style={styles.couponLabel}>Payment date</Text>
              <Text style={styles.couponValue}>{formatDate(coupon.paymentDate)}</Text>
            </View>
            <View style={styles.couponField}>
              <Text style={styles.couponLabel}>Amount</Text>
              <Text style={styles.couponValue}>
                {coupon.amount ? `${coupon.amount}%` : '-'}
              </Text>
            </View>
          </View>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 16,
  },
  separator: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginTop: -8,
    marginBottom: 16,
  },
  nextCouponContainer: {
    marginBottom: 16,
  },
  nextCouponLabel: {
    fontSize: 12,
    color: '#757575',
    marginBottom: 4,
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  dateIndicator: {
    width: 14,
    height: 14,
    borderRadius: 7,
  },
  nextCouponDate: {
    fontSize: 14,
    fontWeight: '600',
    color: '#212121',
  },
  couponItem: {
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  couponRow: {
    flexDirection: 'row',
    gap: 12,
  },
  couponField: {
    flex: 1,
  },
  couponLabel: {
    fontSize: 14,
    color: '#757575',
    marginBottom: 4,
  },
  couponValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#212121',
  },
});
